import type { AxisSelection, FacetBatchResult, IBootstrapAxisValue, IClassificationBootstrap } from '@atlas/types'
import { AxisKind, ValueType } from '@atlas/types'
import type { FilterAxis, FilterAxisValue } from '@atlas/ui-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { partitionAxisSelection } from '@/lib/axis-selection'
import { axisDisplayName } from '@/lib/labels'
import { classificationApi } from '@/services/classification.service'
import { fileService } from '@/services/file.service'
import { DISCOVER_FILES_KEY, useClassificationBootstrap } from './useClassification'
import { useDebouncedValue } from './useDebouncedValue'

// The filter-rail component + its data contract now live in `@atlas/ui-components`
// (promoted from the Library page so the work-order board reuses it). These aliases
// keep the Library hook's original names.
export type RailValue = FilterAxisValue
export type RailAxis = FilterAxis

/** Rail order + labels for the axes shown in the mockup; others sort after, by name. */
const RAIL_AXIS_ORDER: Record<string, number> = {
	geography: 1,
	brand: 2,
	species: 3,
	lifecycle: 4,
	asset_type: 5,
	channel: 6,
	audience: 7,
	business_unit: 8,
}

/**
 * Bootstrap value entries → rail options keyed by CODE (search takes codes).
 * Active-filtering and hierarchy depth are computed server-side (classification-
 * bootstrap) — this is shaping only.
 */
function toOptions(values: IBootstrapAxisValue[]): RailValue[] {
	return values.map((v) => ({ code: v.code, label: v.label, depth: v.depth, meta: v.meta }))
}

/**
 * Registry axes in the `Facet` shape (`{ axis, label, values: [{ code, label }] }`).
 * Lets consumers that were written against the mock-only `/library/facets`
 * endpoint (which has no backend route) read the real axis registry instead —
 * same data, live source.
 */
export function railAxesToFacets(axes: RailAxis[]): { axis: string; label: string; values: RailValue[] }[] {
	return axes.map((a) => ({ axis: a.code, label: a.label, values: a.options }))
}

/**
 * The pickable axes (dimensions AND attributes, list-valued, active) plus their
 * values, from ONE cached bootstrap read (`GET /classification/bootstrap`) — no
 * per-axis fan-out. Nothing hard-codes the axis set; `RAIL_AXIS_ORDER` only orders
 * the mockup's axes, the rest sort after.
 */
function usePickableAxesWithValues() {
	const { data, isLoading } = useClassificationBootstrap()
	const allAxes = data?.axes ?? []
	const valuesByAxis = data?.valuesByAxis ?? {}
	const pickable = allAxes
		.filter(
			(a) =>
				(a.kind === AxisKind.DIMENSION || a.kind === AxisKind.ATTRIBUTE) &&
				a.valueType === ValueType.LIST &&
				a.status === 'active',
		)
		.sort((x, y) => (RAIL_AXIS_ORDER[x.code] ?? 99) - (RAIL_AXIS_ORDER[y.code] ?? 99))
	return { pickable, valuesByAxis, isLoading }
}

/**
 * The codes of the pickable axes registered as ATTRIBUTE kind — what a surface needs to
 * split a rail selection into discovery's `filters` / `attributeFilters` channels
 * (`partitionAxisSelection`). Empty while the registry read is in flight, which reads as
 * "all dimensions" — the same answer the rail gives before it knows its axes.
 */
export function useAttributeAxisCodes(): ReadonlySet<string> {
	const { pickable } = usePickableAxesWithValues()
	return new Set(pickable.filter((a) => a.kind === AxisKind.ATTRIBUTE).map((a) => a.code))
}

/**
 * Every pickable axis WITH all of its values. For surfaces that need to resolve any
 * code to its label regardless of what is on screen — a work order's tuple, a
 * message's tags, a rule's scope — where a per-axis lazy read would leave codes
 * unlabelled. Generalises `usePanelAxes` (dimensions only) so attribute axes
 * (e.g. business_unit) are pickable too.
 */
export function useAxesWithValues(): { axes: RailAxis[]; isLoading: boolean } {
	const { pickable, valuesByAxis, isLoading } = usePickableAxesWithValues()
	const axes: RailAxis[] = pickable.map((a) => ({
		code: a.code,
		label: axisDisplayName(a),
		options: toOptions(valuesByAxis[a.code] ?? []),
		kind: a.kind,
	}))
	return { axes, isLoading }
}

/**
 * Resolve one axis's codes on a tuple to their human label(s) from the axis
 * registry (never a raw code) — e.g. a work order's `geography`, a message's
 * tags, a rule's scope. Returns `undefined` when the tuple has nothing for
 * that axis. Pair with `useAxesWithValues()` for the `axes` argument.
 */
export function resolveTupleLabel(axes: RailAxis[], axisCode: string, tuple: AxisSelection): string | undefined {
	const codes = tuple?.[axisCode]
	if (!codes?.length) return undefined
	const axis = axes.find((a) => a.code === axisCode)
	return codes.map((c) => axis?.options.find((o) => o.code === c)?.label ?? c).join(', ')
}

/**
 * Axes for a filter rail: every pickable axis is offered, but options are only
 * surfaced for the ones currently shown as groups (the axes behind "Add variable"
 * are named without their values, matching the prior reveal-on-demand UX) — though
 * since the bootstrap read already holds every axis's values, revealing one costs
 * no additional request, only a re-render.
 */
export function useFilterRailAxes(shownAxisCodes: string[]): { axes: RailAxis[]; isLoading: boolean } {
	const { pickable, valuesByAxis, isLoading } = usePickableAxesWithValues()
	const axes: RailAxis[] = pickable.map((a) => ({
		code: a.code,
		label: axisDisplayName(a),
		options: shownAxisCodes.includes(a.code) ? toOptions(valuesByAxis[a.code] ?? []) : [],
	}))
	return { axes, isLoading }
}

/** Canonical string key for an `AxisSelection` — a stable react-query cache key regardless of key/value order. */
function selectionSignature(selection: AxisSelection): string {
	return Object.keys(selection)
		.sort()
		.map((axisCode) => `${axisCode}=${[...(selection[axisCode] ?? [])].sort().join(',')}`)
		.join('&')
}

/**
 * A selected value that fell outside the narrowed set stays visible/selected —
 * skip-self already guarantees an axis's OWN selection remains reachable
 * server-side, but a co-selected OTHER axis can still narrow a value out of
 * this list. Re-adding it (from the full bootstrap list, for its label/depth)
 * keeps it clickable to deselect rather than vanishing mid-exploration.
 */
function keepSelectedVisible(
	narrowed: RailValue[],
	selectedCodes: string[],
	allValues: IBootstrapAxisValue[],
): RailValue[] {
	const present = new Set(narrowed.map((o) => o.code))
	const missing = selectedCodes.filter((code) => !present.has(code))
	if (missing.length === 0) return narrowed
	const byCode = new Map(allValues.map((v) => [v.code, v]))
	const restored = missing
		.map((code) => byCode.get(code))
		.filter((v): v is IBootstrapAxisValue => v !== undefined)
		.map((v) => ({ code: v.code, label: v.label, depth: v.depth }))
	return [...narrowed, ...restored]
}

/** Axis→code→depth, built once per bootstrap payload — avoids an O(n) `.find()` per value per render. */
function useAxisDepthIndex(bootstrap: IClassificationBootstrap | undefined): Map<string, Map<string, number>> {
	return useMemo(() => {
		const byAxis = new Map<string, Map<string, number>>()
		for (const [axisCode, values] of Object.entries(bootstrap?.valuesByAxis ?? {})) {
			byAxis.set(axisCode, new Map(values.map((v) => [v.code, v.depth])))
		}
		return byAxis
	}, [bootstrap])
}

/**
 * Pure per-axis option builder for `useFacetedOptions` — extracted so the hook
 * itself stays short. A facet result carries no `depth` (see `FacetValue`), so
 * a narrowed value's depth is looked up from the bootstrap-derived index.
 */
function buildOptionsByAxis(args: {
	axisCodes: string[]
	hasConstraint: boolean
	bootstrap: IClassificationBootstrap | undefined
	facetData: FacetBatchResult | undefined
	depthByAxis: Map<string, Map<string, number>>
	selection: AxisSelection
	keepNarrowedOutSelectionVisible: boolean
}): Record<string, RailValue[]> {
	const { axisCodes, hasConstraint, bootstrap, facetData, depthByAxis, selection, keepNarrowedOutSelectionVisible } =
		args
	const byAxis: Record<string, RailValue[]> = {}
	for (const axisCode of axisCodes) {
		const allValues = bootstrap?.valuesByAxis[axisCode] ?? []
		if (!hasConstraint) {
			byAxis[axisCode] = toOptions(allValues)
			continue
		}
		const depthOf = depthByAxis.get(axisCode)
		const narrowed = toOptions(
			(facetData?.[axisCode] ?? []).map((fv) => ({
				valueId: fv.valueId,
				axisId: '',
				parentId: null,
				code: fv.code,
				label: fv.label,
				meta: {},
				depth: depthOf?.get(fv.code) ?? 0,
			})),
		)
		byAxis[axisCode] = keepNarrowedOutSelectionVisible
			? keepSelectedVisible(narrowed, selection[axisCode] ?? [], allValues)
			: narrowed
	}
	return byAxis
}

/**
 * Debounced `/facet/batch` query for `useFacetedOptions` — extracted so the
 * public hook itself stays short. `candidateObjectIds` is content-keyed and
 * debounced together with `selection` so a rapid burst of either produces ONE
 * call, not one per keystroke/toggle. `hasConstraint` is true whenever there's
 * anything for the endpoint to narrow by — a non-empty `selection`, OR a
 * (possibly empty) `candidateObjectIds`, since that's a real constraint too.
 */
function useDebouncedFacetQuery(
	selection: AxisSelection,
	axisCodes: string[],
	candidateObjectIds: string[] | undefined,
): { data: FacetBatchResult | undefined; isLoading: boolean; isFetching: boolean; hasConstraint: boolean } {
	const selectionKey = selectionSignature(selection)
	const candidateIdsKey = candidateObjectIds ? [...candidateObjectIds].sort().join(',') : undefined
	// ONE shared timer for both — if `selection` and `candidateObjectIds` each
	// had their own `useDebouncedValue`, a change to one wouldn't reset the
	// other's pending timer, so the two could settle at different times and
	// fire an extra call with a stale half of the filter.
	const stableInput = useMemo(() => ({ selection, candidateObjectIds }), [selectionKey, candidateIdsKey])
	const debounced = useDebouncedValue(stableInput, 200)
	const debouncedSelection = debounced.selection
	const debouncedCandidateIds = debounced.candidateObjectIds
	const hasConstraint =
		Object.values(debouncedSelection).some((codes) => codes.length > 0) || debouncedCandidateIds !== undefined

	const facetQuery = useQuery({
		queryKey: [
			'facet-batch',
			selectionSignature(debouncedSelection),
			[...axisCodes].sort().join(','),
			// `undefined` (unconstrained) vs. an explicit `[]` (match nothing) must
			// produce DIFFERENT cache keys — both would otherwise join to the same
			// empty string, colliding two semantically opposite constraints.
			debouncedCandidateIds === undefined ? '∅' : [...debouncedCandidateIds].sort().join(','),
		],
		queryFn: () =>
			classificationApi.facetBatch(debouncedSelection, axisCodes, { candidateObjectIds: debouncedCandidateIds }),
		enabled: hasConstraint && axisCodes.length > 0,
		placeholderData: keepPreviousData,
	})

	return { data: facetQuery.data, isLoading: facetQuery.isLoading, isFetching: facetQuery.isFetching, hasConstraint }
}

/**
 * Faceted (cumulative) options for the given axes under the current selection —
 * shared by any surface that needs cross-filtering (Library, Rules, the New WO
 * form), so faceting logic doesn't fork per page. Empty selection serves options
 * straight from the cached bootstrap (ZERO facet calls); any selection triggers
 * ONE `POST /facet/batch` per (debounced) selection change — not one per field.
 *
 * `opts.keepNarrowedOutSelectionVisible` (default `true`) controls the ONE
 * behavior design.md Decision 7 says diverges per consumer — the hook itself
 * must not bake in either policy:
 *  - `true` (Library's exploratory rail): a value already selected on an axis
 *    stays visible/selected even if the REST of the selection narrows it out
 *    (skip-self keeps it reachable server-side; this is the client-side half
 *    of that same guarantee) — nothing gets silently cleared mid-exploration.
 *  - `false` (the WO form's precise tuple): the returned options are the RAW
 *    narrowed set with no restoration, so a caller's own reset-on-invalidation
 *    logic can tell a genuinely-narrowed-out selection apart from a still-valid
 *    one and clear it — restoring it here would make that logic unable to ever
 *    observe the narrowed-out case.
 *
 * `opts.candidateObjectIds` narrows candidates to this id set BEFORE counting —
 * for a caller (Rules) folding in a constraint from a field the axis system has
 * no notion of (e.g. `rule.severity`, not an axis) that the shared `filter`
 * can't express. Omitted ⇒ unconstrained; an explicit (possibly empty) array is
 * still a real constraint and, alone, is enough to trigger a facet call even
 * with an empty `selection`.
 */
export function useFacetedOptions(
	selection: AxisSelection,
	axisCodes: string[],
	opts: { keepNarrowedOutSelectionVisible?: boolean; candidateObjectIds?: string[] } = {},
): { optionsByAxis: Record<string, RailValue[]>; isLoading: boolean; isFetching: boolean } {
	const { keepNarrowedOutSelectionVisible = true, candidateObjectIds } = opts
	const { data: bootstrap, isLoading: bootstrapLoading } = useClassificationBootstrap()

	// Re-key on CONTENT, not object reference — `selection`/`axisCodes`/
	// `candidateObjectIds` are typically fresh on every render (e.g.
	// `tupleFromForm(form)`, `[...CONST]`, a page-side `.map(id)`), so
	// memoizing off the raw reference would recompute options on every
	// unrelated render (a keystroke elsewhere).
	const selectionKey = selectionSignature(selection)
	const axisCodesKey = [...axisCodes].sort().join(',')
	const candidateIdsKey = candidateObjectIds ? [...candidateObjectIds].sort().join(',') : undefined

	const facetQuery = useDebouncedFacetQuery(selection, axisCodes, candidateObjectIds)
	const depthByAxis = useAxisDepthIndex(bootstrap)
	const optionsByAxis = useMemo(
		() =>
			buildOptionsByAxis({
				axisCodes,
				hasConstraint: facetQuery.hasConstraint,
				bootstrap,
				facetData: facetQuery.data,
				depthByAxis,
				selection,
				keepNarrowedOutSelectionVisible,
			}),
		[
			selectionKey,
			axisCodesKey,
			candidateIdsKey,
			bootstrap,
			facetQuery.data,
			depthByAxis,
			keepNarrowedOutSelectionVisible,
		],
	)

	return {
		optionsByAxis,
		isLoading: bootstrapLoading || (facetQuery.hasConstraint && facetQuery.isLoading),
		// `isFetching` (not `isLoading`) so `keepPreviousData` keeps showing the
		// prior options — a group refetching after a selection change, not one
		// with nothing to show yet.
		isFetching: facetQuery.hasConstraint && facetQuery.isFetching,
	}
}

/**
 * Library FilterRail's axes: every pickable axis is offered (labels/ordering
 * from the bootstrap registry, as `useFilterRailAxes` already does), but shown
 * axes' options come from `useFacetedOptions` — narrowed by the rest of the
 * current selection — instead of the static full value list.
 */
export function useFacetedFilterRailAxes(
	selection: AxisSelection,
	shownAxisCodes: string[],
): { axes: RailAxis[]; isLoading: boolean } {
	const { pickable, isLoading: registryLoading } = usePickableAxesWithValues()
	const { optionsByAxis, isLoading: facetLoading, isFetching } = useFacetedOptions(selection, shownAxisCodes)
	const axes: RailAxis[] = pickable.map((a) => ({
		code: a.code,
		label: axisDisplayName(a),
		options: shownAxisCodes.includes(a.code) ? (optionsByAxis[a.code] ?? []) : [],
		// All shown axes refetch together (one shared batch call per selection
		// change) — `keepPreviousData` keeps their prior options on screen while
		// this says so, rather than the rail flashing empty.
		isLoading: shownAxisCodes.includes(a.code) ? isFetching : undefined,
	}))
	return { axes, isLoading: registryLoading || facetLoading }
}

/** A non-list attribute axis's metadata — enough to render a free-text input for it. */
export interface FreeInputAxis {
	code: string
	name: string
}

/**
 * Attribute axes whose `valueType` is NOT `list` (e.g. a URL/text/date/number
 * attribute like License Link) — axes with no fixed value set, so a picker
 * makes no sense; a form renders a free-text input for each instead, from the
 * SAME bootstrap read every other axis hook already shares (no extra request).
 */
export function useNonListAttributeAxes(): { axes: FreeInputAxis[]; isLoading: boolean } {
	const { data, isLoading } = useClassificationBootstrap()
	const axes = (data?.axes ?? [])
		.filter((a) => a.kind === AxisKind.ATTRIBUTE && a.valueType !== ValueType.LIST && a.status === 'active')
		.map((a) => ({ code: a.code, name: a.name }))
	return { axes, isLoading }
}

/** Files per Library page. Divides evenly into every grid breakpoint (1/2/3/4 columns), so no page ends ragged. */
export const PAGE_SIZE = 24

/**
 * Discover files under the current rail selection (empty = whole accessible library),
 * one page at a time. The rail offers dimension and attribute axes alike, so the
 * selection is split into discovery's two channels before it is sent — an attribute
 * axis left in `filters` would match no file at all.
 *
 * `POST /files/discover` returns a bare array with no total, so "is there another
 * page" is answered by whether a FULL page came back rather than by a count. The
 * cost is one dead `Next` click when the last page happens to be exactly full,
 * which lands on the grid's existing empty state — a smaller price than
 * fabricating a total the endpoint never reported.
 */
export function useDiscoverFiles(selection: AxisSelection, page = 1) {
	const attributeAxisCodes = useAttributeAxisCodes()
	const { filters, attributeFilters } = partitionAxisSelection(selection, attributeAxisCodes)
	const query = useQuery({
		queryKey: [DISCOVER_FILES_KEY, filters, attributeFilters, page],
		queryFn: () => fileService.discover(filters, attributeFilters, PAGE_SIZE, page),
		// The outgoing page stays on screen while the next one loads, so paging
		// does not flash an empty grid between two full ones.
		placeholderData: keepPreviousData,
	})
	const files = query.data ?? []
	return {
		...query,
		files,
		hasNextPage: files.length === PAGE_SIZE,
		hasPreviousPage: page > 1,
	}
}
